import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTenant } from '../contexts/TenantContext';

const SalesStrategy = ({ apiBase }) => {
  const { tenant } = useTenant();

  const [strategies, setStrategies] = useState([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [channelPartners, setChannelPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('strategy'); // strategy, activity, team, meeting
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedStrategyId) {
      fetchStrategyDetails(selectedStrategyId);
    }
  }, [selectedStrategyId]);

  const fetchData = async () => {
    try {
      const [strategiesRes, projectsRes] = await Promise.all([
        axios.get(`${apiBase}/sales-strategy`),
        axios.get(`${apiBase}/projects`)
      ]);
      setStrategies(strategiesRes.data);
      setProjects(projectsRes.data);
      if (strategiesRes.data.length > 0 && !selectedStrategyId) {
        setSelectedStrategyId(strategiesRes.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStrategyDetails = async (strategyId) => {
    if (!strategyId) {
      console.warn('⚠️ No strategy ID provided to fetchStrategyDetails');
      return;
    }
    
    try {
      console.log('🔄 Fetching strategy details for ID:', strategyId);
      const res = await axios.get(`${apiBase}/sales-strategy/${strategyId}`);
      console.log('✅ Strategy details API response received');
      console.log('✅ Response data:', res.data);
      
      if (res.data && res.data.strategy) {
        console.log('✅ Strategy name:', res.data.strategy.strategy_name);
        console.log('✅ Target sales:', res.data.strategy.target_sales);
        console.log('✅ Target revenue:', res.data.strategy.target_revenue);
        console.log('✅ Start date:', res.data.strategy.start_date);
        console.log('✅ End date:', res.data.strategy.end_date);
        console.log('✅ Activities count:', res.data.activities?.length || 0);
        console.log('✅ Team count:', res.data.team?.length || 0);
        
        setSelectedStrategy(res.data);
        console.log('✅ Strategy state updated - component should re-render');
      } else {
        console.error('❌ Invalid strategy data structure');
        console.error('❌ Expected: res.data.strategy');
        console.error('❌ Got:', res.data);
        alert('Strategy details loaded but data structure is invalid. Please check console.');
      }
    } catch (error) {
      console.error('❌ Error fetching strategy details:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Requested URL:', `${apiBase}/sales-strategy/${strategyId}`);
      alert('Error loading strategy details: ' + (error.response?.data?.error || error.message));
    }
  };

  const fetchUsers = async () => {
    try {
      // Assuming there's a users endpoint
      const res = await axios.get(`${apiBase}/users`).catch(() => ({ data: [] }));
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchChannelPartners = async () => {
    try {
      const res = await axios.get(`${apiBase}/channel-partners`);
      setChannelPartners(res.data);
    } catch (error) {
      console.error('Error fetching channel partners:', error);
    }
  };

  const handleCreateStrategy = async (e) => {
    e.preventDefault();
    try {
      // Ensure project_id is a number and all required fields are present
      const projectId = formData.project_id ? (typeof formData.project_id === 'string' ? parseInt(formData.project_id) : formData.project_id) : null;
      
      if (!projectId) {
        alert('Please select a project from the dropdown');
        return;
      }
      
      if (!formData.strategy_name || formData.strategy_name.trim() === '') {
        alert('Please enter a strategy name');
        return;
      }
      
      if (!formData.start_date) {
        alert('Please select a start date');
        return;
      }
      
      if (!formData.end_date) {
        alert('Please select an end date');
        return;
      }
      
      const submitData = {
        strategy_name: formData.strategy_name.trim(),
        project_id: projectId,
        start_date: formData.start_date,
        end_date: formData.end_date,
        target_sales: formData.target_sales ? parseInt(formData.target_sales) : null,
        target_revenue: formData.target_revenue ? parseFloat(formData.target_revenue) : null,
        status: 'draft',
        created_by: 1
      };
      
      console.log('📤 Submitting strategy:', submitData);
      console.log('📤 Selected project:', projects.find(p => p.id === projectId));
      
      console.log('📤 Submitting strategy data:', JSON.stringify(submitData, null, 2));
      
      const response = await axios.post(`${apiBase}/sales-strategy`, submitData);
      console.log('✅ Strategy created successfully:', response.data);
      console.log('✅ Full response:', response);
      console.log('✅ Response status:', response.status);
      
      // Extract ID from response - handle different response formats
      let newStrategyId = null;
      if (response.data) {
        newStrategyId = response.data.id || response.data.strategy?.id;
        console.log('✅ Extracted strategy ID:', newStrategyId);
      }
      
      if (!newStrategyId) {
        console.error('❌ No ID returned from API');
        console.error('❌ Response data structure:', response.data);
        // Still close modal and refresh, user can select manually
        setShowModal(false);
        setFormData({});
        await fetchData();
        alert('Strategy may have been created. Please check the dropdown and select it manually.');
        return;
      }
      
      // Close modal and clear form
      setShowModal(false);
      setFormData({});
      
      // Refresh the strategies list first
      console.log('🔄 Step 1: Refreshing strategies list...');
      await fetchData();
      console.log('✅ Step 1 complete: Strategies list refreshed');
      
      // Set the new strategy as selected
      console.log('🔄 Step 2: Setting selected strategy ID:', newStrategyId);
      setSelectedStrategyId(newStrategyId);
      
      // Wait a moment for state to update
      await new Promise(resolve => setTimeout(resolve, 300));
      console.log('✅ Step 2 complete: Strategy ID set');
      
      // Fetch strategy details
      console.log('🔄 Step 3: Fetching strategy details for ID:', newStrategyId);
      await fetchStrategyDetails(newStrategyId);
      console.log('✅ Step 3 complete: Strategy details fetched');
      
      alert('Sales strategy created successfully! Details are loading...');
    } catch (error) {
      console.error('❌ Error creating strategy:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      alert('Error creating strategy: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${apiBase}/sales-strategy/${selectedStrategyId}/activities`, formData);
      setShowModal(false);
      setFormData({});
      fetchStrategyDetails(selectedStrategyId);
    } catch (error) {
      alert('Error adding activity: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAddTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${apiBase}/sales-strategy/${selectedStrategyId}/team`, formData);
      setShowModal(false);
      setFormData({});
      fetchStrategyDetails(selectedStrategyId);
    } catch (error) {
      alert('Error adding team member: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAddMeeting = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${apiBase}/sales-strategy/${selectedStrategyId}/meetings`, formData);
      setShowModal(false);
      setFormData({});
      fetchStrategyDetails(selectedStrategyId);
    } catch (error) {
      alert('Error scheduling meeting: ' + (error.response?.data?.error || error.message));
    }
  };

  const getActivityTypeColor = (type) => {
    switch (type) {
      case 'ATL': return '#2563eb';
      case 'BTL': return '#10b981';
      case 'Digital': return '#f59e0b';
      case 'Print': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'in_progress': return '#2563eb';
      case 'planned': return '#6b7280';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return <div className="loading">Loading sales strategy...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h1>Sales Strategy Management</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <select
            value={selectedStrategyId || ''}
            onChange={(e) => setSelectedStrategyId(e.target.value ? parseInt(e.target.value) : null)}
            className="form-select"
            style={{ minWidth: '200px' }}
          >
            <option value="">Select Strategy</option>
            {strategies.map(s => (
              <option key={s.id} value={s.id}>{s.strategy_name}</option>
            ))}
          </select>
          <button onClick={() => { 
            setModalType('strategy'); 
            setFormData({});
            fetchUsers();
            // Ensure projects are loaded
            if (projects.length === 0) {
              fetchData();
            }
            setShowModal(true); 
          }} className="btn btn-primary">
            + New Strategy
          </button>
        </div>
      </div>

      {selectedStrategyId && selectedStrategy && selectedStrategy.strategy ? (
        <>
          {/* Strategy Overview */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
              <div>
                <h2 className="card-title">{selectedStrategy.strategy.strategy_name}</h2>
                <p style={{ color: '#6b7280', marginTop: '8px' }}>
                  {selectedStrategy.strategy.project_name} | 
                  {new Date(selectedStrategy.strategy.start_date).toLocaleDateString()} - 
                  {new Date(selectedStrategy.strategy.end_date).toLocaleDateString()}
                </p>
              </div>
              <span className="badge" style={{ backgroundColor: getStatusColor(selectedStrategy.strategy.status) + '20', color: getStatusColor(selectedStrategy.strategy.status) }}>
                {selectedStrategy.strategy.status}
              </span>
            </div>
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              <div className="stat-card">
                <div className="stat-label">Target Sales</div>
                <div className="stat-value">{selectedStrategy.strategy.target_sales || 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Target Revenue</div>
                <div className="stat-value">₹{formatCurrency(selectedStrategy.strategy.target_revenue || 0)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Team Members</div>
                <div className="stat-value">{selectedStrategy.team?.length || 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Activities</div>
                <div className="stat-value">{selectedStrategy.activities?.length || 0}</div>
              </div>
            </div>
          </div>

          {/* Sales Velocity Chart */}
          {selectedStrategy.velocity && selectedStrategy.velocity.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">Sales Velocity</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={selectedStrategy.velocity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="target_sales" stroke="#6b7280" name="Target Sales" strokeDasharray="5 5" />
                  <Line yAxisId="left" type="monotone" dataKey="actual_sales" stroke="#2563eb" name="Actual Sales" />
                  <Line yAxisId="right" type="monotone" dataKey="target_revenue" stroke="#f59e0b" name="Target Revenue (₹)" strokeDasharray="5 5" />
                  <Line yAxisId="right" type="monotone" dataKey="actual_revenue" stroke="#10b981" name="Actual Revenue (₹)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Timeline View */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 className="card-title">Timeline</h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => { setModalType('activity'); setShowModal(true); fetchUsers(); }} className="btn btn-primary" style={{ fontSize: '14px', padding: '8px 16px' }}>
                  + Add Activity
                </button>
                <button onClick={() => { setModalType('meeting'); setShowModal(true); fetchChannelPartners(); }} className="btn btn-success" style={{ fontSize: '14px', padding: '8px 16px' }}>
                  + Schedule Meeting
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selectedStrategy.activities?.map((activity, idx) => (
                <div key={idx} style={{ 
                  padding: '16px', 
                  borderLeft: `4px solid ${getActivityTypeColor(activity.activity_type)}`,
                  background: '#f9fafb',
                  borderRadius: '6px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
                        <span className="badge" style={{ backgroundColor: getActivityTypeColor(activity.activity_type) + '20', color: getActivityTypeColor(activity.activity_type) }}>
                          {activity.activity_type}
                        </span>
                        <span className="badge" style={{ backgroundColor: '#e5e7eb', color: '#374151' }}>
                          {activity.category}
                        </span>
                        <strong>{activity.activity_name}</strong>
                      </div>
                      {activity.description && <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>{activity.description}</p>}
                      <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280' }}>
                        <span>📅 {new Date(activity.start_date).toLocaleDateString()}</span>
                        {activity.end_date && <span>→ {new Date(activity.end_date).toLocaleDateString()}</span>}
                        {activity.assigned_to_name && <span>👤 {activity.assigned_to_name}</span>}
                        {activity.budget > 0 && <span>💰 ₹{formatCurrency(activity.budget)}</span>}
                      </div>
                    </div>
                    <span className="badge" style={{ backgroundColor: getStatusColor(activity.status) + '20', color: getStatusColor(activity.status) }}>
                      {activity.status}
                    </span>
                  </div>
                </div>
              ))}
              {selectedStrategy.meetings?.map((meeting, idx) => (
                <div key={idx} style={{ 
                  padding: '16px', 
                  borderLeft: '4px solid #8b5cf6',
                  background: '#f9fafb',
                  borderRadius: '6px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
                        <span className="badge" style={{ backgroundColor: '#8b5cf6', color: 'white' }}>
                          Meeting
                        </span>
                        <strong>{meeting.channel_partner_name || 'Channel Partner Meeting'}</strong>
                      </div>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280' }}>
                        <span>📅 {new Date(meeting.scheduled_date).toLocaleString()}</span>
                        {meeting.location && <span>📍 {meeting.location}</span>}
                        {meeting.agenda && <span>📋 {meeting.agenda}</span>}
                      </div>
                    </div>
                    <span className="badge" style={{ backgroundColor: getStatusColor(meeting.status) + '20', color: getStatusColor(meeting.status) }}>
                      {meeting.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!selectedStrategy.activities?.length && !selectedStrategy.meetings?.length) && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  No activities or meetings scheduled. Add activities and meetings to build your timeline.
                </div>
              )}
            </div>
          </div>

          {/* Team Management */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 className="card-title">Team Assignments</h2>
              <button onClick={() => { setModalType('team'); setShowModal(true); fetchUsers(); }} className="btn btn-primary" style={{ fontSize: '14px', padding: '8px 16px' }}>
                + Add Team Member
              </button>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Responsibility</th>
                    <th>Period</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedStrategy.team?.map((member, idx) => (
                    <tr key={idx}>
                      <td>{member.user_name}</td>
                      <td>{member.role}</td>
                      <td>{member.responsibility || '-'}</td>
                      <td>
                        {member.start_date && new Date(member.start_date).toLocaleDateString()}
                        {member.end_date && ` - ${new Date(member.end_date).toLocaleDateString()}`}
                      </td>
                      <td>
                        <span className="badge" style={{ backgroundColor: getStatusColor(member.status) + '20', color: getStatusColor(member.status) }}>
                          {member.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!selectedStrategy.team?.length && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  No team members assigned. Add team members to manage responsibilities.
                </div>
              )}
            </div>
          </div>

          {/* ATL/BTL Breakdown */}
          <div className="card">
            <h2 className="card-title">ATL / BTL / Digital / Print Breakdown</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              {['ATL', 'BTL', 'Digital', 'Print'].map(type => {
                const typeActivities = selectedStrategy.activities?.filter(a => a.activity_type === type) || [];
                const totalBudget = typeActivities.reduce((sum, a) => sum + (a.budget || 0), 0);
                return (
                  <div key={type} className="stat-card">
                    <div className="stat-label">{type} Activities</div>
                    <div className="stat-value" style={{ color: getActivityTypeColor(type) }}>{typeActivities.length}</div>
                    <div className="stat-label" style={{ marginTop: '8px' }}>Budget: ₹{formatCurrency(totalBudget)}</div>
                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280' }}>
                      {typeActivities.map((a, i) => (
                        <div key={i} style={{ marginBottom: '4px' }}>• {a.activity_name}</div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            {strategies.length === 0 ? (
              <div>
                <p style={{ marginBottom: '16px', fontSize: '18px' }}>No sales strategies found</p>
                <p style={{ marginBottom: '24px' }}>Create your first sales strategy to get started</p>
                <button onClick={async () => { 
                  setModalType('strategy'); 
                  setFormData({});
                  await fetchUsers();
                  if (projects.length === 0) {
                    try {
                      const res = await axios.get(`${apiBase}/projects`);
                      setProjects(res.data || []);
                    } catch (err) {
                      console.error('Error loading projects:', err);
                    }
                  }
                  setShowModal(true); 
                }} className="btn btn-primary">
                  Create Sales Strategy
                </button>
              </div>
            ) : (
              <p>Please select a strategy from the dropdown above</p>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <StrategyModal
          type={modalType}
          formData={formData}
          setFormData={setFormData}
          onClose={() => { setShowModal(false); setFormData({}); }}
          onSubmit={modalType === 'strategy' ? handleCreateStrategy : 
                   modalType === 'activity' ? handleAddActivity :
                   modalType === 'team' ? handleAddTeam : handleAddMeeting}
          selectedStrategyId={selectedStrategyId}
          projects={projects}
          users={users}
          channelPartners={channelPartners}
          apiBase={apiBase}
        />
      )}
    </div>
  );
};

// Modal Component
const StrategyModal = ({ type, formData, setFormData, onClose, onSubmit, projects, users, channelPartners, selectedStrategyId, apiBase }) => {
  const [localProjects, setLocalProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  
  useEffect(() => {
    // Always fetch projects immediately when modal opens for strategy type
    if (type === 'strategy') {
      const apiBaseUrl = apiBase || '/api';
      console.log('🔄 Modal opened - fetching projects immediately from:', `${apiBaseUrl}/projects`);
      setLoadingProjects(true);
      setLocalProjects([]); // Clear previous data
      
      // Fetch projects immediately
      axios.get(`${apiBaseUrl}/projects`)
        .then(res => {
          const projectsData = res.data || [];
          console.log('✅ Projects loaded immediately:', projectsData.length, 'projects');
          console.log('✅ Project names:', projectsData.map(p => p.name));
          setLocalProjects(projectsData);
          
          const neoBkc = projectsData.length > 0 ? projectsData[0] : null;
          if (neoBkc) {
            console.log('✅ Project found:', neoBkc.name, 'ID:', neoBkc.id);
          } else {
            console.warn('⚠️ Project not found');
          }
        })
        .catch(err => {
          console.error('❌ Error fetching projects:', err);
          console.error('❌ Error response:', err.response?.data);
          // Fallback to props projects if available
          if (projects && projects.length > 0) {
            setLocalProjects(projects);
          }
        })
        .finally(() => {
          setLoadingProjects(false);
        });
    } else {
      setLocalProjects(projects || []);
      setLoadingProjects(false);
    }
  }, [type]); // Only depend on type, fetch immediately when modal opens
  
  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };
  
  const projectsToShow = localProjects.length > 0 ? localProjects : (projects || []);
  
  // Debug logging
  useEffect(() => {
    if (type === 'strategy') {
      console.log('📊 Projects state:', {
        localProjects: localProjects.length,
        propsProjects: projects?.length || 0,
        projectsToShow: projectsToShow.length,
        loading: loadingProjects
      });
    }
  }, [localProjects, projects, projectsToShow, loadingProjects, type]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h3 className="modal-title">
            {type === 'strategy' && 'Create Sales Strategy'}
            {type === 'activity' && 'Add Activity'}
            {type === 'team' && 'Add Team Member'}
            {type === 'meeting' && 'Schedule Meeting'}
          </h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit}>
          {type === 'strategy' && (
            <>
              <div className="form-group">
                <label className="form-label">Strategy Name</label>
                <input
                  type="text"
                  value={formData.strategy_name || ''}
                  onChange={(e) => handleChange('strategy_name', e.target.value)}
                  className="form-input"
                  required
                  placeholder="e.g., Project Launch Strategy"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Project *</label>
                {loadingProjects ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#6b7280', background: '#f9fafb', borderRadius: '6px' }}>
                    🔄 Loading projects...
                  </div>
                ) : (
                  <>
                    <select
                      value={formData.project_id || ''}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        console.log('Project dropdown changed:', selectedValue);
                        console.log('Available projects:', projectsToShow);
                        handleChange('project_id', selectedValue);
                      }}
                      className="form-select"
                      required
                      style={{ 
                        borderColor: formData.project_id ? '#10b981' : '#d1d5db',
                        backgroundColor: formData.project_id ? '#f0fdf4' : 'white'
                      }}
                    >
                      <option value="">Select Project</option>
                      {projectsToShow.length > 0 ? (
                        projectsToShow.map(p => (
                          <option key={p.id} value={p.id.toString()}>{p.name}</option>
                        ))
                      ) : (
                        <option value="" disabled>No projects available</option>
                      )}
                    </select>
                    {formData.project_id && projectsToShow.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#10b981', fontWeight: '500' }}>
                        ✓ Selected: {projectsToShow.find(p => p.id.toString() === formData.project_id.toString())?.name || 'Unknown'}
                      </div>
                    )}
                    {!formData.project_id && !loadingProjects && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#ef4444' }}>
                        ⚠ Please select a project
                      </div>
                    )}
                    {projectsToShow.length > 0 && !loadingProjects && (
                      <div style={{ marginTop: '8px', fontSize: '11px', color: '#6b7280' }}>
                        {projectsToShow.length} project(s) available
                      </div>
                    )}
                    {projectsToShow.length === 0 && !loadingProjects && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#f59e0b', padding: '8px', background: '#fef3c7', borderRadius: '4px' }}>
                        ⚠ No projects found. Please add a project first.
                      </div>
                    )}
                  </>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Target Sales</label>
                  <input
                    type="number"
                    value={formData.target_sales || ''}
                    onChange={(e) => handleChange('target_sales', e.target.value)}
                    className="form-input"
                    placeholder="Number of units"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Target Revenue (₹)</label>
                  <input
                    type="number"
                    value={formData.target_revenue || ''}
                    onChange={(e) => handleChange('target_revenue', e.target.value)}
                    className="form-input"
                    placeholder="Total revenue target"
                  />
                </div>
              </div>
            </>
          )}

          {type === 'activity' && (
            <>
              <div className="form-group">
                <label className="form-label">Activity Type</label>
                <select
                  value={formData.activity_type || ''}
                  onChange={(e) => handleChange('activity_type', e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select Type</option>
                  <option value="ATL">ATL (Above The Line)</option>
                  <option value="BTL">BTL (Below The Line)</option>
                  <option value="Digital">Digital</option>
                  <option value="Print">Print</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <input
                  type="text"
                  value={formData.category || ''}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="form-input"
                  required
                  placeholder="e.g., TV Ads, Radio, Events, Social Media"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Activity Name</label>
                <input
                  type="text"
                  value={formData.activity_name || ''}
                  onChange={(e) => handleChange('activity_name', e.target.value)}
                  className="form-input"
                  required
                  placeholder="e.g., Prime Time TV Campaign"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Activity details..."
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Budget (₹)</label>
                  <input
                    type="number"
                    value={formData.budget || ''}
                    onChange={(e) => handleChange('budget', e.target.value)}
                    className="form-input"
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Assigned To</label>
                  <select
                    value={formData.assigned_to || ''}
                    onChange={(e) => handleChange('assigned_to', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select Team Member</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  value={formData.priority || 'medium'}
                  onChange={(e) => handleChange('priority', e.target.value)}
                  className="form-select"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </>
          )}

          {type === 'team' && (
            <>
              <div className="form-group">
                <label className="form-label">Team Member</label>
                <select
                  value={formData.user_id || ''}
                  onChange={(e) => handleChange('user_id', e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select Team Member</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <input
                  type="text"
                  value={formData.role || ''}
                  onChange={(e) => handleChange('role', e.target.value)}
                  className="form-input"
                  required
                  placeholder="e.g., Sales Manager, Marketing Lead, ATL Coordinator"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Responsibility</label>
                <textarea
                  value={formData.responsibility || ''}
                  onChange={(e) => handleChange('responsibility', e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Describe their responsibilities..."
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </>
          )}

          {type === 'meeting' && (
            <>
              <div className="form-group">
                <label className="form-label">Channel Partner</label>
                <select
                  value={formData.channel_partner_id || ''}
                  onChange={(e) => handleChange('channel_partner_id', e.target.value)}
                  className="form-select"
                >
                  <option value="">All Partners (General Meeting)</option>
                  {channelPartners.map(cp => (
                    <option key={cp.id} value={cp.id}>{cp.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Meeting Type</label>
                <select
                  value={formData.meeting_type || ''}
                  onChange={(e) => handleChange('meeting_type', e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select Type</option>
                  <option value="Onboarding">Onboarding</option>
                  <option value="Training">Training</option>
                  <option value="Review">Review</option>
                  <option value="Strategy">Strategy</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Scheduled Date & Time</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_date || ''}
                  onChange={(e) => handleChange('scheduled_date', e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Location</label>
                <input
                  type="text"
                  value={formData.location || ''}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="form-input"
                  placeholder="Meeting venue"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Agenda</label>
                <textarea
                  value={formData.agenda || ''}
                  onChange={(e) => handleChange('agenda', e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Meeting agenda..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Attendees</label>
                <input
                  type="text"
                  value={formData.attendees || ''}
                  onChange={(e) => handleChange('attendees', e.target.value)}
                  className="form-input"
                  placeholder="Comma-separated list of attendees"
                />
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0
  }).format(amount);
};

export default SalesStrategy;

